import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export type PulseAxis = 'active' | 'signup';

export interface PulseStat {
  window: string; // AudienceWindow
  label: string;
  value: number | null | undefined;
}

/**
 * A dense, single-row strip of clickable stats — replaces the old grid of
 * giant one-number MUI cards. Each cell is a compact label + number that
 * highlights on hover and opens the drill-in when clicked.
 */
const ActivityPulse: React.FC<{
  title: string;
  axis: PulseAxis;
  dotColor: string;
  stats: PulseStat[];
  onStatClick: (axis: PulseAxis, window: string, label: string) => void;
}> = ({ title, axis, dotColor, stats, onStatClick }) => {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <FiberManualRecordIcon sx={{ fontSize: 10, color: dotColor }} />
        <Typography variant="overline" sx={{ letterSpacing: 0.5, lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {stats.map((s, i) => {
          const v = s.value ?? 0;
          const clickable = v > 0;
          return (
            <Tooltip
              key={s.window}
              title={clickable ? `View the ${v.toLocaleString()} — click for details` : 'No one yet'}
              arrow
            >
              <Box
                onClick={clickable ? () => onStatClick(axis, s.window, s.label) : undefined}
                sx={{
                  flex: '1 1 0',
                  minWidth: 92,
                  px: 1.5,
                  py: 1.25,
                  borderLeft: i === 0 ? 'none' : '1px solid',
                  borderColor: 'divider',
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'background-color 0.12s',
                  '&:hover': clickable ? { bgcolor: 'action.hover' } : {},
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ lineHeight: 1.1, color: clickable ? 'text.primary' : 'text.disabled' }}
                >
                  {(s.value ?? '—').toLocaleString?.() ?? '—'}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {s.label}
                  {clickable ? ' ›' : ''}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Paper>
  );
};

export default ActivityPulse;
